import type { ReactNode } from 'react';
import { cn } from '../lib/cn';
import type { IconComponent } from '../lib/types';

export type BadgeTone =
  'neutral' | 'info' | 'success' | 'warning' | 'danger' | 'high' | 'medium' | 'low';

interface BadgeBase {
  children: ReactNode;
  className?: string | undefined;
}

/**
 * NFR-11: "colour is never the sole carrier of meaning in legends and status".
 *
 * That is enforced here rather than in review. A neutral badge is just a chip
 * and needs no icon; any tone that means something — a status, a severity —
 * requires one, so a red badge whose only difference from a green one is the
 * red is a type error, not a finding in an accessibility audit six months from
 * now.
 */
export type BadgeProps =
  | (BadgeBase & { tone?: 'neutral' | undefined; icon?: IconComponent | undefined })
  | (BadgeBase & { tone: Exclude<BadgeTone, 'neutral'>; icon: IconComponent });

const TONES: Record<BadgeTone, string> = {
  neutral: 'bg-surface-raised text-fg-muted border-line',
  info: 'bg-info-soft text-info border-info/30',
  success: 'bg-success-soft text-success border-success/30',
  warning: 'bg-warning-soft text-warning border-warning/30',
  danger: 'bg-danger-soft text-danger border-danger/30',
  high: 'bg-severity-high-soft text-severity-high border-severity-high/30',
  medium: 'bg-severity-medium-soft text-severity-medium border-severity-medium/30',
  low: 'bg-severity-low-soft text-severity-low border-severity-low/30',
};

export function Badge({ tone = 'neutral', icon: Icon, children, className }: BadgeProps) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full border px-2 py-0.5',
        'text-xs font-medium whitespace-nowrap',
        TONES[tone],
        className,
      )}
    >
      {Icon === undefined ? null : <Icon aria-hidden className="size-3" />}
      {children}
    </span>
  );
}
